import { useEffect, useMemo, useState } from 'react';
import { useBudget } from '../store/BudgetContext';
import { Category } from '../api/types';
import { theme } from '../theme';
import { Icon, categoryIconName } from '../components/Icon';
import { CategoryPlanPad } from '../components/CategoryPlanPad';
import { CategoryCreateForm } from '../components/CategoryCreateForm';
import { CategoryEditor } from './CategoryEditor';
import { fmtPlanNum } from '../utils/plan-pad';
import { planDailyLimit, planEnvelopeSum } from '../utils/plan-limit';
import { plural } from '../utils/plural';
import { CategoriesLegacy } from './CategoriesLegacy';
import { OrderTab } from './CategoriesOrder';
import { CheckTab } from './CategoriesCheck';

/** Флаг отката: false вернёт дореформенный экран-модалку. */
const NEW_CATEGORIES_SCREEN = true;

export function Categories(props: { onBack: () => void; initialTab?: 'list' | 'order' | 'check' }) {
  if (!NEW_CATEGORIES_SCREEN) return <CategoriesLegacy onBack={props.onBack} />;
  return <CategoriesScreen {...props} />;
}

// ---------------------------------------------------------------------------
// Секции
// ---------------------------------------------------------------------------

type SectionKey = 'daily' | 'envelope' | 'income';

interface Section {
  key: SectionKey;
  title: string;
  cats: Category[];
}

function buildSections(cats: Category[]): Section[] {
  const sorted = cats.slice().sort((a, b) => a.sort_order - b.sort_order);
  const daily = sorted.filter((c) => c.type === 'Расход' && c.is_daily);
  const envelope = sorted.filter((c) => c.type === 'Расход' && !c.is_daily);
  const income = sorted.filter((c) => c.type === 'Доход');

  const sections: Section[] = [];
  if (daily.length) sections.push({ key: 'daily', title: 'Повседневные — в дневном лимите', cats: daily });
  if (envelope.length) sections.push({ key: 'envelope', title: 'Нерегулярные — конверты месяца', cats: envelope });
  if (income.length) sections.push({ key: 'income', title: 'Доходные', cats: income });
  return sections;
}

// ---------------------------------------------------------------------------
// Экран
// ---------------------------------------------------------------------------

type TabKey = 'Список' | 'Порядок' | 'Сверка';

const INITIAL_TAB_MAP: Record<'list' | 'order' | 'check', TabKey> = {
  list: 'Список',
  order: 'Порядок',
  check: 'Сверка',
};

function CategoriesScreen({ onBack, initialTab }: { onBack: () => void; initialTab?: 'list' | 'order' | 'check' }) {
  const { viewData, updateCategory } = useBudget();
  const [activeTab, setActiveTab] = useState<TabKey>(
    initialTab ? INITIAL_TAB_MAP[initialTab] : 'Список',
  );
  const [planEditId, setPlanEditId] = useState<number | null>(null);
  const [editId, setEditId] = useState<number | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [pendingPlanFor, setPendingPlanFor] = useState<string | null>(null);

  const cats = viewData?.categories ?? [];
  const sections = useMemo(() => buildSections(cats), [cats]);

  // Когда появится созданная категория — открыть PlanPad
  useEffect(() => {
    if (!pendingPlanFor || !viewData) return;
    const found = viewData.categories.find((c) => c.name === pendingPlanFor);
    if (found) {
      setPendingPlanFor(null);
      setPlanEditId(found.id);
    }
  }, [viewData, pendingPlanFor]);

  // Карта: name → количество операций (один раз на весь список, useMemo)
  const opCountMap = useMemo<Map<string, number>>(() => {
    const ops = viewData?.operations ?? [];
    const m = new Map<string, number>();
    for (const o of ops) {
      m.set(o.category, (m.get(o.category) ?? 0) + 1);
    }
    return m;
  }, [viewData?.operations]);

  const dailyLimit = planDailyLimit(cats);

  function handleToggleEdit(id: number) {
    // Открытие редактора закрывает numpad и наоборот — один блок за раз
    if (editId === id) {
      setEditId(null);
    } else {
      setEditId(id);
      setPlanEditId(null);
      setShowCreateForm(false);
    }
  }

  function handleTogglePlan(id: number) {
    setPlanEditId((prev) => (prev === id ? null : id));
    setEditId(null);
    setShowCreateForm(false);
  }

  function handleShowCreate() {
    setShowCreateForm(true);
    setPlanEditId(null);
    setEditId(null);
  }

  const { sub, ink, line, subSoft } = theme.colors;

  return (
    <div style={{ padding: '26px 18px 40px' }}>
      {/* Шапка */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          onClick={onBack}
          style={{
            background: 'transparent',
            border: 'none',
            color: sub,
            padding: 2,
            cursor: 'pointer',
          }}
        >
          <Icon name="chevronLeft" size={18} />
        </button>
        <div>
          <div
            style={{
              textTransform: 'uppercase',
              letterSpacing: '.18em',
              fontSize: 11,
              color: sub,
            }}
          >
            настройки · категории
          </div>
          <h1 style={{ fontSize: 24, margin: '2px 0 0' }}>Категории</h1>
        </div>
      </div>

      {/* Табы */}
      <div
        style={{
          display: 'flex',
          gap: 16,
          marginTop: 14,
          borderBottom: `1px solid ${line}`,
        }}
      >
        {(['Список', 'Порядок', 'Сверка'] as const).map((label) => {
          const active = activeTab === label;
          return (
            <button
              key={label}
              onClick={() => {
                if (active) return;
                // Закрываем открытые numpad/редактор/форму создания при смене вкладки
                setPlanEditId(null);
                setEditId(null);
                setShowCreateForm(false);
                setActiveTab(label);
              }}
              style={{
                background: 'transparent',
                border: 'none',
                textTransform: 'uppercase',
                letterSpacing: '.14em',
                fontSize: 11,
                padding: '0 2px 7px',
                marginBottom: -1,
                cursor: active ? 'default' : 'pointer',
                color: active ? ink : sub,
                borderBottom: active ? `1.5px solid ${ink}` : '1.5px solid transparent',
                fontFamily: theme.font,
              }}
            >
              {label}
            </button>
          );
        })}
      </div>

      {/* Сводка — только на вкладке «Список» */}
      {activeTab === 'Список' && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            marginTop: 14,
          }}
        >
          <span
            style={{
              textTransform: 'uppercase',
              letterSpacing: '.18em',
              fontSize: 12,
              color: sub,
            }}
          >
            дневной лимит из планов
          </span>
          <span
            style={{
              fontFamily: theme.fontDisplay,
              fontVariationSettings: "'opsz' 144, 'WONK' 0",
              fontVariantNumeric: 'tabular-nums',
              fontSize: 20,
              whiteSpace: 'nowrap',
            }}
          >
            ≈{fmtPlanNum(dailyLimit)} ₽/день
          </span>
        </div>
      )}

      {/* Вкладка «Порядок» */}
      {activeTab === 'Порядок' && viewData && (
        <OrderTab
          key="order-tab"
          initialCats={cats}
          updateCategory={updateCategory}
        />
      )}

      {/* Вкладка «Сверка» — key при монтировании перезапускает проход */}
      {activeTab === 'Сверка' && (
        <div style={{ marginTop: 16 }}>
          <CheckTab
            key="check-tab"
            onDone={() => setActiveTab('Список')}
          />
        </div>
      )}

      {/* Вкладка «Список» */}
      {activeTab === 'Список' && (
        <>
          {/* Три секции */}
          {sections.map((section) => (
            <SectionBlock
              key={section.key}
              section={section}
              allCats={cats}
              opCountMap={opCountMap}
              planEditId={planEditId}
              editId={editId}
              onTogglePlan={handleTogglePlan}
              onClosePlan={() => setPlanEditId(null)}
              onToggleEdit={handleToggleEdit}
              onCloseEdit={() => setEditId(null)}
            />
          ))}

          {/* Кнопка «+ добавить категорию» или форма создания */}
          <div style={{ marginTop: 14 }}>
            {showCreateForm ? (
              <CategoryCreateForm
                onCancel={() => setShowCreateForm(false)}
                onCreated={(name) => {
                  setShowCreateForm(false);
                  setPendingPlanFor(name);
                }}
              />
            ) : (
              <button
                type="button"
                onClick={handleShowCreate}
                style={{
                  width: '100%',
                  padding: '11px 0',
                  background: 'transparent',
                  border: `1px dashed ${subSoft}`,
                  borderRadius: theme.radius.sm,
                  color: sub,
                  fontSize: 14,
                  cursor: 'pointer',
                  fontFamily: theme.font,
                }}
              >
                + добавить категорию
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Секция
// ---------------------------------------------------------------------------

function SectionBlock({
  section,
  allCats,
  opCountMap,
  planEditId,
  editId,
  onTogglePlan,
  onClosePlan,
  onToggleEdit,
  onCloseEdit,
}: {
  section: Section;
  allCats: Category[];
  opCountMap: Map<string, number>;
  planEditId: number | null;
  editId: number | null;
  onTogglePlan: (id: number) => void;
  onClosePlan: () => void;
  onToggleEdit: (id: number) => void;
  onCloseEdit: () => void;
}) {
  const { sub, ink } = theme.colors;
  const isEnvelope = section.key === 'envelope';

  return (
    <div>
      {/* Заголовок секции */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          borderBottom: `1px solid ${ink}`,
          padding: '16px 0 8px',
        }}
      >
        <span
          style={{
            textTransform: 'uppercase',
            letterSpacing: '.14em',
            fontSize: 11,
            color: sub,
          }}
        >
          {section.title}
        </span>
        {isEnvelope && (
          <span
            style={{
              fontSize: 12,
              color: sub,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {fmtPlanNum(planEnvelopeSum(allCats))} ₽
          </span>
        )}
      </div>

      {/* Строки категорий */}
      {section.cats.map((c) => (
        <CategoryRow
          key={c.id}
          category={c}
          allCats={allCats}
          opCount={opCountMap.get(c.name) ?? 0}
          planEditId={planEditId}
          editId={editId}
          onTogglePlan={onTogglePlan}
          onClosePlan={onClosePlan}
          onToggleEdit={onToggleEdit}
          onCloseEdit={onCloseEdit}
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Строка категории
// ---------------------------------------------------------------------------

function CategoryRow({
  category,
  allCats,
  opCount,
  planEditId,
  editId,
  onTogglePlan,
  onClosePlan,
  onToggleEdit,
  onCloseEdit,
}: {
  category: Category;
  allCats: Category[];
  opCount: number;
  planEditId: number | null;
  editId: number | null;
  onTogglePlan: (id: number) => void;
  onClosePlan: () => void;
  onToggleEdit: (id: number) => void;
  onCloseEdit: () => void;
}) {
  const { sub, ink, line, subSoft, green } = theme.colors;
  const isPlanOpen = planEditId === category.id;
  const isEditOpen = editId === category.id;

  // Подстрока: grp · N операций · в квестах
  const subParts: string[] = [];
  if (category.grp) subParts.push(category.grp);
  subParts.push(`${opCount} ${plural(opCount, ['операция', 'операции', 'операций'])}`);
  if (category.is_hot) subParts.push('в квестах');
  const subLine = subParts.join(' · ');

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '12px 0',
          borderBottom: `1px solid ${line}`,
        }}
      >
        {/* Иконка */}
        <span style={{ flexShrink: 0 }}>
          <Icon name={categoryIconName(category)} size={15} color={sub} />
        </span>

        {/* Кнопка-имя → открывает/закрывает редактор */}
        <button
          onClick={() => onToggleEdit(category.id)}
          style={{
            flex: 1,
            minWidth: 0,
            textAlign: 'left',
            background: 'transparent',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
          }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 15,
              color: ink,
            }}
          >
            {category.name}
            {category.is_hot ? (
              <Icon name="feather" size={12} color={green} inline />
            ) : null}
          </div>
          <div style={{ fontSize: 11, color: sub, marginTop: 1 }}>{subLine}</div>
        </button>

        {/* Кнопка-план */}
        <button
          onClick={() => onTogglePlan(category.id)}
          style={{
            background: 'transparent',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
          }}
        >
          <span
            style={{
              fontFamily: theme.fontDisplay,
              fontVariationSettings: "'opsz' 144, 'WONK' 0",
              fontVariantNumeric: 'tabular-nums',
              fontSize: 16,
              color: category.plan_month === 0 ? sub : ink,
              borderBottom: `1px dashed ${subSoft}`,
              paddingBottom: 1,
            }}
          >
            {fmtPlanNum(category.plan_month)}
          </span>
        </button>
      </div>

      {/* Инлайн CategoryPlanPad */}
      {isPlanOpen && (
        <div style={{ margin: '10px 0' }}>
          <CategoryPlanPad
            category={category}
            cats={allCats}
            onClose={onClosePlan}
          />
        </div>
      )}

      {/* Инлайн CategoryEditor */}
      {isEditOpen && (
        <CategoryEditor
          category={category}
          opsCount={opCount}
          cats={allCats}
          onClose={onCloseEdit}
        />
      )}
    </>
  );
}
